import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Navigation } from "./components/Navigation";
import { MobileLayout } from "./components/MobileLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Pages
import { LoginPage } from "./pages/LoginPage";
import { WalletPage } from "./pages/WalletPage";
import { MobileWalletPage } from "./pages/MobileWalletPage";
import { UserProfilePage } from "./pages/UserProfilePage";
import { AnalyticsDashboard } from "./pages/AnalyticsDashboard";
import { AccountSettings } from "./pages/AccountSettings";
import { AdminDashboard } from "./pages/AdminDashboard";
import { ClubResourcesPage } from "./pages/ClubResourcesPage";
import { ClubAccounting } from "./pages/ClubAccounting";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PushSettingsPage } from "./pages/PushSettingsPage";

// Admin Sub-pages
import { UserManagementPage } from "./pages/admin/UserManagementPage";
import { IssueGiftcardsPage } from "./pages/admin/IssueGiftcardsPage";
import { QRScannerPage } from "./pages/admin/QRScannerPage";
import { ClubAnalyticsPage } from "./pages/admin/ClubAnalyticsPage";

// System Pages (CLUB admin only)
import { ClubManagementPage } from "./pages/system/ClubManagementPage";
import { StaffManagementPage } from "./pages/system/StaffManagementPage";
import { SystemReportsPage } from "./pages/system/SystemReportsPage";
import { AuditLogPage } from "./pages/system/AuditLogPage";

import "./App.css";

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected Routes - All Authenticated Users */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <Navigate to="/wallet" replace />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <MobileLayoutWrapper>
                <MobileWalletPage />
              </MobileLayoutWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MobileLayoutWrapper>
                <UserProfilePage />
              </MobileLayoutWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path="/resources"
          element={
            <ProtectedRoute>
              <MobileLayoutWrapper>
                <ClubResourcesPage />
              </MobileLayoutWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Layout>
                <AnalyticsDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <AccountSettings />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/push-settings"
          element={
            <ProtectedRoute>
              <MobileLayoutWrapper>
                <PushSettingsPage />
              </MobileLayoutWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "STAFF", "CLUB"]}>
              <Layout>
                <AdminDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "STAFF", "CLUB"]}>
              <Layout>
                <UserManagementPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/giftcards"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "STAFF", "CLUB"]}>
              <Layout>
                <IssueGiftcardsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/qr-scanner"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "STAFF", "CLUB"]}>
              <Layout>
                <QRScannerPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requiredRoles={["ADMIN", "STAFF", "CLUB"]}>
              <Layout>
                <ClubAnalyticsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* System Routes - CLUB only */}
        <Route
          path="/club/accounting"
          element={
            <ProtectedRoute requiredRoles={["CLUB"]}>
              <Layout>
                <ClubAccounting />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/system/clubs"
          element={
            <ProtectedRoute requiredRoles={["CLUB"]}>
              <Layout>
                <ClubManagementPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/system/staff"
          element={
            <ProtectedRoute requiredRoles={["CLUB"]}>
              <Layout>
                <StaffManagementPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/system/reports"
          element={
            <ProtectedRoute requiredRoles={["CLUB"]}>
              <Layout>
                <SystemReportsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/system/audit"
          element={
            <ProtectedRoute requiredRoles={["CLUB"]}>
              <Layout>
                <AuditLogPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* 404 Not Found */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

// Layout component with Navigation - Desktop version
function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navigation />
      <div style={{ flex: 1, width: "100%" }}>{children}</div>
    </div>
  );
}

// Mobile Layout Wrapper
function MobileLayoutWrapper({ children }: { children: React.ReactNode }) {
  return <MobileLayout>{children}</MobileLayout>;
}

export default App;
